import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { ButtonLink, Button, TextField } from "@/components/ui/FormControls";
import { api } from "@/lib/api";
import { WorkspaceMembersPanel, type WorkspaceMember } from "@/components/workspace/WorkspaceMembersPanel";

interface ProjectSummary {
  id: string;
  name: string;
  siteName: string;
  createdAt: string;
  updatedAt: string;
  imports: number;
  runs: number;
}

interface WorkspacePayload {
  workspace: { id: string; name: string };
  projects: ProjectSummary[];
}

export function WorkspacePage() {
  const [data, setData] = useState<WorkspacePayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [projectName, setProjectName] = useState("");
  const [siteName, setSiteName] = useState("");
  const [creating, setCreating] = useState(false);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);

  async function load() {
    try {
      setError(null);
      setData(await api<WorkspacePayload>("/workspace"));
    } catch (e) {
      setError(e instanceof Error ? e.message : "작업공간을 불러오지 못했습니다.");
    }
  }

  async function loadMembers() {
    try {
      const response = await api<{ members: WorkspaceMember[] }>("/workspace/members");
      setMembers(response.members);
    } catch {
      setMembers([]);
    }
  }

  useEffect(() => { void load(); void loadMembers(); }, []);

  async function createProject(event: FormEvent) {
    event.preventDefault();
    if (!projectName.trim() || !siteName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      await api("/workspace/projects", { method: "POST", json: { name: projectName, siteName } });
      setProjectName("");
      setSiteName("");
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "프로젝트를 만들지 못했습니다.");
    } finally {
      setCreating(false);
    }
  }

  return (
    <AppShell>
      <div className="page">
        <PageHeader title="회사 작업공간" description="실제 활동자료를 연결하고, 같은 기준으로 산정을 반복하는 운영공간입니다." />
        {error && <p className="note note-danger text-caption" role="alert">{error}</p>}
        {!data && !error && <p className="text-caption text-ink-subtle">작업공간을 준비하는 중입니다…</p>}
        {data && (
          <>
            <section className="workspace-hero">
              <div>
                <p className="workspace-kicker">WORKSPACE</p>
                <h2 className="workspace-hero-title">{data.workspace.name}</h2>
                <p className="workspace-hero-desc">개인 시나리오와 분리된 회사 프로젝트, 데이터, 산정 실행 기록입니다.</p>
              </div>
              <div className="workspace-hero-flow" aria-label="기업 산정 흐름">
                <span>데이터 연결</span><span aria-hidden>→</span><span>검증</span><span aria-hidden>→</span><span>산정 기록</span>
              </div>
            </section>

            <div className="workspace-section-head">
              <div><h2 className="workspace-card-title">프로젝트 <span className="workspace-count">{data.projects.length}개</span></h2><p className="workspace-card-desc">사업장이나 공급경로별로 반복 산정을 관리합니다.</p></div>
            </div>
            <div className="workspace-project-grid">
              {data.projects.map((project) => (
                <article key={project.id} className="workspace-project-card">
                  <div className="workspace-project-card-head"><div><h3>{project.name}</h3><p>{project.siteName}</p></div><span className="badge-accent">운영 중</span></div>
                  <dl className="workspace-project-stats"><div><dt>가져온 파일</dt><dd>{project.imports}</dd></div><div><dt>산정 실행</dt><dd>{project.runs}</dd></div></dl>
                  <ButtonLink to={`/workspace/projects/${project.id}`} variant="primary" fullWidth>프로젝트 열기</ButtonLink>
                </article>
              ))}
            </div>

            <section className="workspace-card">
              <div className="workspace-card-head"><div><h2 className="workspace-card-title">새 프로젝트</h2><p className="workspace-card-desc">실제 데이터를 연결할 사업장이나 공급경로를 추가합니다.</p></div></div>
              <form className="workspace-form-grid workspace-form-grid-compact" onSubmit={createProject}>
                <TextField label="프로젝트 이름" placeholder="예: 2026년 울산 수전해 실증" value={projectName} onChange={(event) => setProjectName(event.target.value)} />
                <TextField label="사업장" placeholder="예: 울산 생산시설" value={siteName} onChange={(event) => setSiteName(event.target.value)} />
                <div className="workspace-actions workspace-actions-end"><Button variant="primary" type="submit" disabled={creating || !projectName.trim() || !siteName.trim()}>{creating ? "만드는 중…" : "프로젝트 추가"}</Button></div>
              </form>
            </section>
            <WorkspaceMembersPanel members={members} onChanged={() => void loadMembers()} />
          </>
        )}
      </div>
    </AppShell>
  );
}
